import { useContext, useMemo, useState } from "react";
import { Table, Select, DatePicker, InputNumber, Checkbox, Input, Button, Form, message, Popconfirm } from "antd";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useProjects } from "../../queries/projects";
import { useTasks } from "../../queries/tasks";
import { useUsers } from "../../queries/users";
import { useTimeEntries, useCreateTimeEntry, useUpdateTimeEntry, useDeleteTimeEntry } from "../../queries/timeEntries";
import { ProjectProps, SessionUser, TaskProps, TimeEntryProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { StatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import styles from "./timesheet.module.css";

const saveErrorMessage = (err: unknown) => (err instanceof ApiError ? err.message : "Could not save the change");
const hrs = (m: number) => (m / 60).toFixed(1);
const dayDate = (value: string) => dayjs(value.slice(0, 10));

export const formatDayDate = (value: string, format: string) => dayDate(value).format(format);

export const TimesheetPage = () => {
  const { userData, isManager } = useContext(AuthContext);
  const [viewingAsUserId, setViewingAsUserId] = useState<string | undefined>(undefined);
  const effectiveUserId = viewingAsUserId ?? userData?._id;

  const { data: users } = useUsers({ enabled: isManager });
  const { data: projects } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>();
  const { data: tasks } = useTasks({ projectId: selectedProjectId || undefined, enabled: !!selectedProjectId });

  const { data: entries, isLoading, isError, error } = useTimeEntries({ userId: effectiveUserId });
  const createEntry = useCreateTimeEntry();
  const updateEntry = useUpdateTimeEntry();
  const deleteEntry = useDeleteTimeEntry();

  const [form] = Form.useForm();

  const totals = useMemo(() => {
    const list = entries || [];
    const weekStart = dayjs().startOf("week");
    const week = list.filter((e: TimeEntryProps) => dayDate(e.date).isAfter(weekStart.subtract(1, "day")));
    return {
      week: week.reduce((n: number, e: TimeEntryProps) => n + e.durationMinutes, 0),
      billable: week.filter((e: TimeEntryProps) => e.billable).reduce((n: number, e: TimeEntryProps) => n + e.durationMinutes, 0),
      today: list.filter((e: TimeEntryProps) => dayDate(e.date).isSame(dayjs(), "day")).reduce((n: number, e: TimeEntryProps) => n + e.durationMinutes, 0),
    };
  }, [entries]);

  const projectName = (pid: string) => (projects || []).find((p: ProjectProps) => p._id === pid)?.name;

  const handleLog = async () => {
    const values = await form.validateFields();
    try {
      await createEntry.mutateAsync({
        taskId: values.taskId,
        date: values.date.format("YYYY-MM-DD"),
        durationMinutes: values.durationMinutes,
        billable: values.billable ?? true,
        note: values.note,
        ...(isManager && viewingAsUserId ? { userId: viewingAsUserId } : {}),
      });
      form.resetFields();
      message.success("Time logged");
    } catch (err) {
      message.error(saveErrorMessage(err));
    }
  };

  return (
    <PageShell
      kind="list"
      kicker="Time"
      title={viewingAsUserId ? `Timesheet — ${(users || []).find((u: SessionUser) => u._id === viewingAsUserId)?.name}` : "My time"}
      actions={
        isManager && (
          <Select
            style={{ width: 220 }}
            placeholder="Viewing as (me)"
            allowClear
            value={viewingAsUserId}
            onChange={setViewingAsUserId}
            options={(users || []).map((u: SessionUser) => ({ label: u.name, value: u._id }))}
          />
        )
      }
    >
      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className="osi-kicker">Today</div>
          <div className={styles.statValue}>{hrs(totals.today)}h</div>
          <div className={styles.statNote}>{dayjs().format("dddd D MMMM")}</div>
        </div>
        <div className={styles.stat}>
          <div className="osi-kicker">This week</div>
          <div className={styles.statValue}>{hrs(totals.week)}h</div>
          <div className={styles.statNote}>since {dayjs().startOf("week").format("D MMM")}</div>
        </div>
        <div className={styles.stat}>
          <div className="osi-kicker">Billable</div>
          <div className={styles.statValue}>{hrs(totals.billable)}h</div>
          <div className={styles.statNote}>
            {Math.round((totals.billable / Math.max(totals.week, 1)) * 100)}% of this week
          </div>
        </div>
      </div>

      <Form form={form} layout="vertical" className={styles.logbar}>
        <div className={styles.logTitle}>Log an entry</div>
        <Form.Item name="projectId" label="Project" rules={[{ required: true }]}>
          <Select
            style={{ width: 180 }}
            options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
            onChange={(value) => {
              setSelectedProjectId(value);
              form.setFieldValue("taskId", undefined);
            }}
          />
        </Form.Item>
        <Form.Item name="taskId" label="Task" rules={[{ required: true }]}>
          <Select
            style={{ width: 220 }}
            showSearch
            optionFilterProp="label"
            disabled={!selectedProjectId}
            placeholder={selectedProjectId ? "Pick a task" : "Pick a project first"}
            options={(tasks || []).map((t: TaskProps) => ({ label: t.title, value: t._id }))}
          />
        </Form.Item>
        <Form.Item name="date" label="Date" rules={[{ required: true }]} initialValue={dayjs()}>
          <DatePicker />
        </Form.Item>
        <Form.Item
          name="durationMinutes"
          label="Minutes"
          rules={[
            { required: true, message: "Enter the number of minutes" },
            { type: "number", min: 1, message: "Enter at least 1 minute" },
          ]}
        >
          <InputNumber style={{ width: 100 }} />
        </Form.Item>
        <Form.Item name="note" label="Note">
          <Input placeholder="What did you do?" style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="billable" valuePropName="checked" initialValue={true} label=" ">
          <Checkbox>Billable</Checkbox>
        </Form.Item>
        <Form.Item label=" ">
          <Button type="primary" onClick={handleLog} loading={createEntry.isPending}>Log time</Button>
        </Form.Item>
      </Form>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<TimeEntryProps>
          rowKey="_id"
          size="small"
          dataSource={entries || []}
          pagination={(entries || []).length > 30 ? { pageSize: 30, size: "small" } : false}
          locale={{
            emptyText: (
              <EmptyState
                title="No time logged yet"
                description="Use the row above to log your first entry. Entries roll up into Reports automatically."
              />
            ),
          }}
          columns={[
            {
              title: "Date", dataIndex: "date", width: 130,
              render: (v) => <span className="osi-num">{formatDayDate(v, "ddd D MMM")}</span>,
            },
            {
              title: "Project", dataIndex: "projectId", width: 180,
              render: (pid: string) => <span style={{ color: "var(--osi-text-muted)" }}>{projectName(pid) || "—"}</span>,
            },
            {
              title: "Duration", dataIndex: "durationMinutes", width: 120,
              render: (value, entry) => (
                <InputNumber
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  min={1}
                  defaultValue={value}
                  onBlur={(e) => {
                    const parsed = Number(e.target.value);
                    if (e.target.value === "" || Number.isNaN(parsed)) return;
                    updateEntry.mutate(
                      { id: entry._id, data: { durationMinutes: parsed } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    );
                  }}
                />
              ),
            },
            {
              title: "Billable", dataIndex: "billable", width: 110,
              render: (v: boolean) => v
                ? <StatusTag label="Billable" tone="ok" />
                : <StatusTag label="Internal" tone="neutral" />,
            },
            {
              title: "Note", dataIndex: "note", responsive: ["lg"],
              render: (v?: string) => v || <span style={{ color: "var(--osi-text-faint)" }}>—</span>,
            },
            {
              title: "", width: 90, align: "right",
              render: (_, entry) => (
                <Popconfirm
                  title="Delete this time entry?"
                  description="This action cannot be undone."
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteEntry.mutate(entry._id, { onError: (err) => message.error(saveErrorMessage(err)) })}
                >
                  <Button danger size="small" aria-label="Delete time entry">Delete</Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      </DisplayComponent>
    </PageShell>
  );
};
